import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useSearchParams } from 'react-router-dom';

const DataEntry = () => {
    const [devices, setDevices] = useState([]);
    const [selectedDevice, setSelectedDevice] = useState('');
    const [entryMode, setEntryMode] = useState('single'); // 'single' lub 'monthly'
    const [selectedDate, setSelectedDate] = useState(() => {
        const today = new Date().toISOString().split('T')[0];
        return today;
    });
    const [selectedMonth, setSelectedMonth] = useState(() => {
        const today = new Date();
        return today.toISOString().slice(0, 7); // "YYYY-MM"
    });
    const [selectedDay, setSelectedDay] = useState(1);
    const [daysInMonth, setDaysInMonth] = useState(31);
    const [monthlyData, setMonthlyData] = useState({});
    const [dataPoints, setDataPoints] = useState([]);
    const [searchParams] = useSearchParams();

    // Stany do śledzenia postępu przy wysyłaniu danych w trybie miesięcznym
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentDay, setCurrentDay] = useState(0);
    const [totalDays, setTotalDays] = useState(0);

    // Stany związane z importem pliku CSV (tylko w trybie miesięcznym)
    const [filePPEName, setFilePPEName] = useState('');
    const [fileMonth, setFileMonth] = useState('');
    const [isPPEMatch, setIsPPEMatch] = useState(null);
    const [isMonthMatch, setIsMonthMatch] = useState(null);

    // Nowe stany do obsługi usuwania danych
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteMessage, setDeleteMessage] = useState(null);
    const [deleteError, setDeleteError] = useState(null);

    // Definiowanie fetchDevices poza useEffect
    const fetchDevices = async () => {
        try {
            const response = await api.get('/devices/');
            setDevices(response.data);
        } catch (error) {
            console.error('Błąd: Nie udało się pobrać listy urządzeń.', error);
        }
    };

    useEffect(() => {
        // Pobieranie listy urządzeń z backendu
        fetchDevices();

        // Jeśli w URL mamy device_id, ustawiamy je jako wybrane
        const deviceIdFromURL = searchParams.get('device_id');
        if (deviceIdFromURL) {
            setSelectedDevice(deviceIdFromURL);
        }
    }, [searchParams]);

    // Pomocnicza funkcja formatująca datę jako "YYYY-MM-DD"
    const formatDate = (dateObj) => {
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Obliczanie liczby godzin w dobie (obsługa zmiany czasu: 23/24/25 h)
    const getHoursInDay = (dateObj) => {
        const testDate = new Date(dateObj.getTime());
        testDate.setHours(0, 0, 0, 0);

        // Zakładamy strefę "Europe/Warsaw"
        const timeZone = 'Europe/Warsaw';
        const fmt = new Intl.DateTimeFormat('pl-PL', {
            timeZone,
            timeZoneName: 'short',
        });

        const startOfDay = new Date(testDate);
        const endOfDay = new Date(testDate);
        endOfDay.setHours(24);

        const startParts = fmt.formatToParts(startOfDay);
        const endParts = fmt.formatToParts(endOfDay);

        const startOffset = startParts.find((part) => part.type === 'timeZoneName').value;
        const endOffset = endParts.find((part) => part.type === 'timeZoneName').value;

        if (startOffset !== endOffset) {
            // Nastąpiła zmiana czasu
            const startOffsetMin = startOfDay.getTimezoneOffset();
            const endOffsetMin = endOfDay.getTimezoneOffset();
            if (endOffsetMin > startOffsetMin) {
                // Cofnięcie zegarów -> 25 godzin
                return 25;
            } else {
                // Przyspieszenie zegarów -> 23 godziny
                return 23;
            }
        }
        // Standardowy dzień -> 24 godz.
        return 24;
    };

    // Inicjalizacja pustych godzin (np. 24) z pustymi polami
    const initializeDayData = (dateObj, hoursCount) => {
        return Array.from({ length: hoursCount }, (_, hourIndex) => ({
            hour: hourIndex + 1, // od 1 do n
            production: '',
            consumption: '',
            balance: '',
        }));
    };

    // Reakcja na zmianę trybu (single / monthly) lub zmiany w selectedMonth
    useEffect(() => {
        if (entryMode === 'monthly') {
            const [year, month] = selectedMonth.split('-').map(Number);
            const monthIndex = month - 1;
            // Ile dni ma dany miesiąc
            const date = new Date(year, monthIndex + 1, 0); // ostatni dzień w tym miesiącu
            const days = date.getDate();
            setDaysInMonth(days);

            // Dopasowujemy selectedDay, by nie był > days
            if (selectedDay > days) {
                setSelectedDay(days);
            }

            // Jeśli monthlyData jest puste -> inicjalizujemy
            if (Object.keys(monthlyData).length === 0) {
                const initialData = {};
                for (let d = 1; d <= days; d++) {
                    const dayDate = new Date(year, monthIndex, d);
                    const hoursInDay = getHoursInDay(dayDate);
                    initialData[d] = initializeDayData(dayDate, hoursInDay);
                }
                setMonthlyData(initialData);
            }
        }
    }, [entryMode, selectedMonth, selectedDay, monthlyData]);

    // Za każdym razem, gdy zmienia się selectedDay (w monthly) lub selectedDate (w single), odświeżamy dataPoints
    useEffect(() => {
        if (entryMode === 'monthly') {
            const [year, month] = selectedMonth.split('-').map(Number);
            const monthIndex = month - 1;
            const dateObj = new Date(year, monthIndex, selectedDay);

            if (monthlyData[selectedDay]) {
                setDataPoints(monthlyData[selectedDay]);
            } else {
                // Brak w monthlyData -> tworzymy od nowa
                const hoursInDay = getHoursInDay(dateObj);
                const newDataPoints = initializeDayData(dateObj, hoursInDay);
                setMonthlyData((prev) => ({ ...prev, [selectedDay]: newDataPoints }));
                setDataPoints(newDataPoints);
            }
        } else {
            // Tryb single
            const dateObj = new Date(selectedDate);
            const hoursInDay = getHoursInDay(dateObj);
            const newDataPoints = initializeDayData(dateObj, hoursInDay);
            setDataPoints(newDataPoints);
        }
    }, [entryMode, selectedDay, monthlyData, selectedMonth, selectedDate]);

    // Funkcja sprawdzająca, czy dane dla danego urządzenia istnieją dla wybranego miesiąca
    const hasDataForMonth = (device) => {
        if (!device.uploaded_dates) return false;
        const [year, month] = selectedMonth.split('-').map(Number);
        return device.uploaded_dates.some(date => {
            const [y, m] = date.split('-').map(Number);
            return y === year && m === month;
        });
    };

    // Znajdowanie obiektu wybranego urządzenia
    const selectedDeviceObj = devices.find(d => d.id === selectedDevice);

    // Sprawdzenie, czy dane istnieją dla wybranego PPE i miesiąca
    const dataExists = selectedDeviceObj && hasDataForMonth(selectedDeviceObj); // <--- Dodane

    // Funkcja sprawdzająca czy urządzenie jest aktywne dla wprowadzanej daty
    const isDeviceActiveForDate = (device) => {
        // Brak daty dezaktywacji = urządzenie aktywne
        if (!device.deactivation_date) return true;

        // Określamy datę dla której wprowadzamy dane
        let currentDataDate;
        if (entryMode === 'single') {
            currentDataDate = selectedDate;
        } else {
            // W trybie monthly: pierwszy dzień wybranego miesiąca
            const [year, month] = selectedMonth.split('-');
            currentDataDate = `${year}-${month}-01`;
        }

        // Urządzenie aktywne jeśli data wprowadzania < data dezaktywacji
        return currentDataDate < device.deactivation_date;
    };

    // Efekt resetujący wybrane urządzenie jeśli nie jest już aktywne dla wybranej daty
    useEffect(() => {
        if (selectedDevice && devices.length > 0) {
            const device = devices.find(d => d.id === selectedDevice);
            if (device) {
                // Sprawdzamy czy urządzenie jest aktywne dla aktualnie wybranej daty
                const shouldBeActive = !device.deactivation_date ||
                    (entryMode === 'single'
                        ? selectedDate < device.deactivation_date
                        : `${selectedMonth}-01` < device.deactivation_date);

                if (!shouldBeActive) {
                    setSelectedDevice('');
                }
            }
        }
    }, [selectedDate, selectedMonth, entryMode, selectedDevice, devices]);

    // Zmiana miesiąca
    const handleMonthChange = (newVal) => {
        setSelectedMonth(newVal);
        setSelectedDay(1);
        setMonthlyData({});
    };

    // Zmiana daty w trybie single
    const handleDateChange = (newVal) => {
        setSelectedDate(newVal);
    };

    // Zmiana dnia w trybie monthly (strzałki)
    const handleDayChange = (direction) => {
        if (direction === 'prev' && selectedDay > 1) {
            setSelectedDay((prev) => prev - 1);
        } else if (direction === 'next' && selectedDay < daysInMonth) {
            setSelectedDay((prev) => prev + 1);
        }
    };

    // Obsługa wpisywania liczb w polach "consumption"/"production"
    const handleInputChange = (e, hourIndex, field) => {
        const updated = [...dataPoints];
        updated[hourIndex][field] = e.target.value;

        const prodVal = parseFloat(updated[hourIndex].production.replace(',', '.'));
        const consVal = parseFloat(updated[hourIndex].consumption.replace(',', '.'));
        if (!isNaN(prodVal) && !isNaN(consVal)) {
            updated[hourIndex].balance = (consVal - prodVal).toString();
        } else {
            updated[hourIndex].balance = '';
        }

        setDataPoints(updated);

        if (entryMode === 'monthly') {
            setMonthlyData((prev) => ({ ...prev, [selectedDay]: updated }));
        }
    };

    // Obsługa wgrywania pliku CSV (tylko monthly)
    const handleFileImport = (event) => {
        if (entryMode !== 'monthly') return;

        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (ev) => {
                const text = ev.target.result;
                parseCSVData(text);
            };
            reader.readAsText(file);
        }
    };

    // Główna funkcja parsowania CSV
    const parseCSVData = (rawText) => {
        if (entryMode !== 'monthly') return;

        const lines = rawText.trim().split('\n');
        const nonEmptyLines = lines.filter((line) => line.trim() !== '');

        // Pierwsze dwie linie -> nazwa PPE i numer miesiąca
        const ppeLine = nonEmptyLines[0]?.split(';') || [];
        const monthLine = nonEmptyLines[1]?.split(';') || [];

        const filePPE = ppeLine[1]?.trim() || '';
        const fileMon = monthLine[1]?.trim() || '';
        setFilePPEName(filePPE);
        setFileMonth(fileMon);

        // Czy nazwa PPE i miesiąc z pliku pokrywają się z wybranym PPE i month
        const selectedDevName = devices.find((d) => d.id === selectedDevice)?.name || '';
        setIsPPEMatch(filePPE === selectedDevName);

        const selectedMonthNum = parseInt(selectedMonth.split('-')[1], 10);
        setIsMonthMatch(parseInt(fileMon, 10) === selectedMonthNum);

        // Znajdujemy wiersz z nagłówkiem ("dzień/godzina" itp.)
        const headerIndex = nonEmptyLines.findIndex((ln) =>
            ln.toLowerCase().startsWith('dzie')
        );
        if (headerIndex === -1) {
            alert('Błąd: Nie znaleziono nagłówka godzin (np. "dzień/godzina") w pliku.');
            return;
        }

        // Tutaj definiujemy year, monthIndex i daysInSelMonth do użytku w poniższej pętli
        const [year, mm] = selectedMonth.split('-').map(Number);
        const monthIndex = mm - 1;
        const daysInSelMonth = new Date(year, monthIndex + 1, 0).getDate();

        let currentLineIndex = headerIndex + 1;
        const dataObj = {};
        const importedDays = new Set(); // do wykrywania duplikatów i sprawdzenia liczby dni

        while (currentLineIndex < nonEmptyLines.length) {
            // Wiersz CP
            const cpLine = nonEmptyLines[currentLineIndex];
            // Wiersz CO
            const coLine = nonEmptyLines[currentLineIndex + 1];
            // Trzeci wiersz CB (pomijamy)
            currentLineIndex += 3;

            if (!cpLine || !coLine) {
                // Niepełny blok CP+CO -> kończymy
                break;
            }

            const cpVals = cpLine.split(';');
            const coVals = coLine.split(';');

            // Numer dnia w CP (kolumna 0)
            const dayStr = cpVals[0]?.trim() || '';
            if (!dayStr) {
                alert(`Błąd: W wierszu CP brakuje numeru dnia.\nTreść wiersza:\n${cpLine}`);
                return;
            }

            const day = parseInt(dayStr, 10);
            if (isNaN(day)) {
                alert(`Błąd: Numer dnia "${dayStr}" nie jest liczbą (wiersz CP).`);
                return;
            }

            if (day < 1 || day > 31) {
                alert(`Błąd: Numer dnia "${dayStr}" jest spoza zakresu [1..31].`);
                return;
            }

            // Sprawdzamy, czy mieści się w faktycznej liczbie dni wybranego miesiąca
            if (day > daysInSelMonth) {
                alert(
                    `Błąd: W pliku znaleziono dzień = ${day}, ale ten miesiąc ma tylko ${daysInSelMonth} dni.`
                );
                return;
            }

            // Wykrywanie duplikatów dnia
            if (importedDays.has(day)) {
                alert(`Błąd: Dzień ${day} pojawia się więcej niż raz w pliku. Anulowano import.`);
                return;
            }
            importedDays.add(day);

            // Ustalamy liczbę godzin w tym konkretnym dniu
            const dateForDay = new Date(year, monthIndex, day);
            const hoursInDay = getHoursInDay(dateForDay);

            // Pomocnicza funkcja do wyciągnięcia wartości godzin
            const parseValues = (vals) => {
                // Pomijamy 2 pierwsze kolumny (numer dnia i "CP"/"CO"), bierzemy resztę
                const raw = vals.slice(2).filter((v) => v.trim() !== '');
                return raw.map((item) => {
                    let tmp = item.trim();
                    // Usuwamy ",+"
                    tmp = tmp.replace(/, *\+/g, '');
                    // Zamieniamy ',' na '.'
                    tmp = tmp.replace(',', '.');
                    return tmp;
                });
            };

            const consumptionVals = parseValues(cpVals);
            const productionVals = parseValues(coVals);

            if (
                consumptionVals.length !== hoursInDay ||
                productionVals.length !== hoursInDay
            ) {
                alert(
                    `Błąd: Dzień ${day} powinien mieć ${hoursInDay} godzin, ` +
                    `ale linie CP/CO mają inną liczbę wartości.`
                );
                return;
            }

            // Tworzymy dayData
            const dayData = Array.from({ length: hoursInDay }, (_, i) => {
                const cStr = consumptionVals[i];
                const pStr = productionVals[i];
                const cVal = parseFloat(cStr);
                const pVal = parseFloat(pStr);
                if (isNaN(cVal) || isNaN(pVal)) {
                    alert(
                        `Błąd: Dzień ${day}, godzina ${i + 1} zawiera niepoprawną wartość liczbową.`
                    );
                    throw new Error('Niepoprawna liczba – przerywamy import.');
                }
                const bal = cVal - pVal;
                return {
                    hour: i + 1,
                    consumption: cStr,
                    production: pStr,
                    balance: bal.toString(),
                };
            });

            dataObj[day] = dayData;
        }

        // Sprawdzamy, czy wczytaliśmy dokładnie daysInSelMonth dni
        if (importedDays.size !== daysInSelMonth) {
            alert(
                `Błąd: W pliku występują dane dla ${importedDays.size} dni, ` +
                `a wybrany miesiąc ma ${daysInSelMonth} dni. Anulowano import.`
            );
            return;
        }

        // Wszystko OK -> zapisujemy
        setMonthlyData(dataObj);
        setSelectedDay(1);
        alert('Import pliku CSV zakończony powodzeniem.');
    };

    // Wklejanie danych ze schowka (może działać zarówno w trybie single, jak i monthly)
    const handlePasteData = async () => {
        try {
            const text = await navigator.clipboard.readText();
            const lines = text.trim().split('\n');
            if (lines.length < 2) {
                alert(
                    'Błąd: Oczekiwano dwóch linii (Energia Pobrana i Energia Oddana). ' +
                    'Nieprawidłowe dane w schowku.'
                );
                return;
            }

            const parseLine = (ln) => {
                return ln
                    .trim()
                    .split('\t')
                    .map((val) => val.trim().replace(/[,+]+$/, '').replace(',', '.'));
            };

            let dateObj;
            if (entryMode === 'monthly') {
                const [y, m] = selectedMonth.split('-').map(Number);
                dateObj = new Date(y, m - 1, selectedDay);
            } else {
                dateObj = new Date(selectedDate);
            }
            const hoursInDay = getHoursInDay(dateObj);

            const consumptionArr = parseLine(lines[0]);
            const productionArr = parseLine(lines[1]);
            if (consumptionArr.length !== hoursInDay || productionArr.length !== hoursInDay) {
                alert(
                    `Błąd: Ten dzień ma ${hoursInDay} godzin, a wklejono inną liczbę wartości.`
                );
                return;
            }

            const updated = Array.from({ length: hoursInDay }, (_, i) => {
                const cVal = parseFloat(consumptionArr[i]);
                const pVal = parseFloat(productionArr[i]);
                if (isNaN(cVal) || isNaN(pVal)) {
                    throw new Error(
                        `Błąd: Godzina ${i + 1} zawiera niepoprawną liczbę w danych schowka.`
                    );
                }
                const bal = cVal - pVal;
                return {
                    hour: i + 1,
                    consumption: consumptionArr[i],
                    production: productionArr[i],
                    balance: bal.toString(),
                };
            });

            setDataPoints(updated);
            if (entryMode === 'monthly') {
                setMonthlyData((prev) => ({ ...prev, [selectedDay]: updated }));
            }

            alert('Dane ze schowka wklejono poprawnie.');
        } catch (error) {
            console.error('Błąd podczas wklejania danych:', error);
            alert(
                'Błąd: Nie udało się wkleić danych. Sprawdź, czy przeglądarka umożliwia dostęp do schowka.'
            );
        }
    };

    // Zatwierdzanie danych (single / monthly)
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedDevice) {
            alert('Błąd: Nie wybrano urządzenia (PPE).');
            return;
        }

        if (entryMode === 'single') {
            // Walidacja
            for (let dp of dataPoints) {
                if (dp.consumption === '' || dp.production === '') {
                    alert('Błąd: Uzupełnij wszystkie godziny (Energia Pobrana i Oddana).');
                    return;
                }
                const c = parseFloat(dp.consumption.replace(',', '.'));
                const p = parseFloat(dp.production.replace(',', '.'));
                if (isNaN(c) || isNaN(p)) {
                    alert('Błąd: Wprowadzone wartości nie są liczbami.');
                    return;
                }
            }
            const payload = prepareSinglePayload();

            try {
                await api.post('/data-points/daily', payload);
                alert('Dane dla pojedynczego dnia zostały przesłane pomyślnie.');
                // Reset
                setSelectedDevice('');
                setSelectedDate(new Date().toISOString().split('T')[0]);
                setDataPoints([]);
            } catch (error) {
                const errorMsg =
                    error.response?.data?.detail ||
                    error.response?.data?.message ||
                    'Wystąpił błąd podczas przesyłania danych.';
                alert(`Błąd: ${errorMsg}`);
                console.error('Błąd przy przesyłaniu (single):', error);
            }
        } else {
            // Tryb monthly

            // Walidacja wszystkich dni
            for (let dayKey in monthlyData) {
                const dayData = monthlyData[dayKey];
                // Ile godzin powinno być?
                const [yy, mm] = selectedMonth.split('-').map(Number);
                const dateObj = new Date(yy, mm - 1, parseInt(dayKey));
                const neededHours = getHoursInDay(dateObj);

                if (dayData.length !== neededHours) {
                    alert(
                        `Błąd: Dzień ${dayKey} ma niewłaściwą liczbę godzin (powinno być ${neededHours}).`
                    );
                    return;
                }

                for (let dp of dayData) {
                    if (dp.consumption === '' || dp.production === '') {
                        alert(`Błąd: Dzień ${dayKey}: Proszę uzupełnić wszystkie pola.`);
                        return;
                    }
                    const c = parseFloat(dp.consumption.replace(',', '.'));
                    const p = parseFloat(dp.production.replace(',', '.'));
                    if (isNaN(c) || isNaN(p)) {
                        alert(`Błąd: Dzień ${dayKey}: Wpisano niepoprawne liczby.`);
                        return;
                    }
                }
            }

            const payloadArray = prepareMonthlyPayload();
            setTotalDays(payloadArray.length);
            setCurrentDay(0);
            setIsSubmitting(true);

            try {
                for (const dayItem of payloadArray) {
                    await api.post('/data-points/daily', dayItem);
                    setCurrentDay((prev) => prev + 1);
                }
                alert('Dane miesięczne zostały przesłane pomyślnie.');
                // Reset
                setSelectedDevice('');
                const nextMonth = new Date().toISOString().slice(0, 7);
                setSelectedMonth(nextMonth);
                setSelectedDay(1);
                setMonthlyData({});
                setDataPoints([]);
                setFilePPEName('');
                setFileMonth('');
                setIsPPEMatch(null);
                setIsMonthMatch(null);
                setIsSubmitting(false);
                setCurrentDay(0);
                setTotalDays(0);
            } catch (error) {
                const errMsg =
                    error.response?.data?.detail ||
                    error.response?.data?.message ||
                    'Wystąpił błąd przy przesyłaniu danych.';
                alert(`Błąd: ${errMsg}`);
                console.error('Błąd przy przesyłaniu (monthly):', error);
                setIsSubmitting(false);
                setCurrentDay(0);
                setTotalDays(0);
            }
        }
    };

    // Tworzenie payloadu dla pojedynczego dnia
    const prepareSinglePayload = () => {
        const date = selectedDate;
        const mapped = dataPoints.map((dp) => ({
            hour: dp.hour - 1,
            production: parseFloat(dp.production.replace(',', '.')) || 0,
            consumption: parseFloat(dp.consumption.replace(',', '.')) || 0,
            balance: parseFloat(dp.balance) || 0,
        }));
        return { device_id: selectedDevice, date, data_points: mapped };
    };

    // Tworzenie tablicy payloadów (po 1 dla każdego dnia) w trybie monthly
    const prepareMonthlyPayload = () => {
        const [yy, mm] = selectedMonth.split('-').map(Number);
        const monthIndex = mm - 1;

        return Object.entries(monthlyData).map(([dayKey, dayData]) => ({
            device_id: selectedDevice,
            date: formatDate(new Date(yy, monthIndex, parseInt(dayKey))),
            data_points: dayData.map((dp) => ({
                hour: dp.hour - 1,
                production: parseFloat(dp.production.replace(',', '.')) || 0,
                consumption: parseFloat(dp.consumption.replace(',', '.')) || 0,
                balance: parseFloat(dp.balance) || 0,
            })),
        }));
    };

    // Funkcja wyświetlająca datę do suwaka dni (np. "Dzień 3: 3 kwietnia 2024")
    const formatSelectedDate = () => {
        const [yy, mm] = selectedMonth.split('-').map(Number);
        const dateObj = new Date(yy, mm - 1, selectedDay);
        return dateObj.toLocaleDateString('pl-PL', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });
    };

    // Nowa funkcja do obsługi usuwania danych
    const handleDelete = async () => {
        if (!selectedDevice) {
            alert('Błąd: Nie wybrano urządzenia (PPE) do usunięcia.');
            return;
        }

        // Określenie zakresu dat na podstawie trybu
        let startDateStr, endDateStr;
        if (entryMode === 'single') {
            startDateStr = selectedDate;
            endDateStr = selectedDate;
        } else {
            const [year, month] = selectedMonth.split('-').map(Number);
            const firstDay = formatDate(new Date(year, month - 1, 1));
            const lastDay = formatDate(new Date(year, month, 0));
            startDateStr = firstDay;
            endDateStr = lastDay;
        }

        // Potwierdzenie przed usunięciem
        const confirmation = window.confirm(
            `Czy na pewno chcesz usunąć dane dla urządzenia "${selectedDeviceObj?.name || selectedDevice}" ` +
            `od ${startDateStr} do ${endDateStr}? Operacja jest nieodwracalna.`
        );

        if (!confirmation) {
            return;
        }

        setIsDeleting(true);
        setDeleteMessage(null);
        setDeleteError(null);

        try {
            await api.delete(`/data-points/device/${selectedDevice}`, {
                params: {
                    start_date: startDateStr,
                    end_date: endDateStr,
                },
            });

            setDeleteMessage('Dane zostały pomyślnie usunięte.');

            // Odświeżanie listy urządzeń po usunięciu danych
            await fetchDevices();

            // Opcjonalnie: Resetowanie danych w interfejsie
            if (entryMode === 'single') {
                setDataPoints([]);
                setSelectedDate(new Date().toISOString().split('T')[0]);
            } else {
                setMonthlyData({});
                setSelectedDay(1);
                setDataPoints([]);
            }
        } catch (error) {
            const errorMsg =
                error.response?.data?.detail ||
                error.response?.data?.message ||
                'Wystąpił błąd podczas usuwania danych.';
            setDeleteError(`Błąd: ${errorMsg}`);
            console.error('Błąd przy usuwaniu danych:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col items-center p-6">
            <div className="bg-white p-6 rounded-lg shadow-lg max-w-full w-full">
                <h2 className="text-2xl font-bold mb-6">Wprowadzanie danych</h2>
                <form onSubmit={handleSubmit}>
                    {/* Przełącznik trybu (jednodniowy / miesięczny) */}
                    <div className="mb-4">
                        <label className="block text-gray-700 mb-2">
                            Tryb wprowadzania danych:
                        </label>
                        <div className="flex space-x-4">
                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    value="single"
                                    checked={entryMode === 'single'}
                                    onChange={(e) => setEntryMode(e.target.value)}
                                    className="mr-2"
                                />
                                Jednodniowy
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    value="monthly"
                                    checked={entryMode === 'monthly'}
                                    onChange={(e) => setEntryMode(e.target.value)}
                                    className="mr-2"
                                />
                                Miesięczny
                            </label>
                        </div>
                    </div>

                    {/* Wybór PPE i daty/miesiąca */}
                    <div className="mb-4 flex flex-wrap">
                        <div className="mr-4">
                            <label htmlFor="device" className="block text-gray-700 mb-2">
                                PPE:
                            </label>
                            <select
                                id="device"
                                value={selectedDevice}
                                onChange={(e) => setSelectedDevice(e.target.value)}
                                className="w-64 p-2 border border-gray-300 rounded-lg"
                            >
                                <option value="">Wybierz PPE</option>
                                {devices.filter(dev => isDeviceActiveForDate(dev)).map((dev) => {
                                    const dataExistsForDevice = hasDataForMonth(dev);
                                    return (
                                        <option
                                            key={dev.id}
                                            value={dev.id}
                                            title={
                                                dataExistsForDevice
                                                    ? 'Dane dla tego miesiąca już wprowadzone'
                                                    : 'Brak danych dla tego miesiąca'
                                            }
                                        >
                                            {dev.name} {dataExistsForDevice ? '✔️' : ''}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>

                        {entryMode === 'single' ? (
                            <div>
                                <label htmlFor="date" className="block text-gray-700 mb-2">
                                    Data:
                                </label>
                                <input
                                    type="date"
                                    id="date"
                                    value={selectedDate}
                                    onChange={(e) => handleDateChange(e.target.value)}
                                    className="w-64 p-2 border border-gray-300 rounded-lg"
                                />
                            </div>
                        ) : (
                            <div>
                                <label className="block text-gray-700 mb-2">
                                    Miesiąc:
                                </label>
                                <div className="flex space-x-2">
                                    <select 
                                        className="p-2 border border-gray-300 rounded-lg"
                                        value={selectedMonth.split('-')[1]}
                                        onChange={(e) => {
                                            const year = selectedMonth.split('-')[0];
                                            const newMonth = e.target.value.padStart(2, '0');
                                            handleMonthChange(`${year}-${newMonth}`);
                                        }}
                                    >
                                        <option value="01">Styczeń</option>
                                        <option value="02">Luty</option>
                                        <option value="03">Marzec</option>
                                        <option value="04">Kwiecień</option>
                                        <option value="05">Maj</option>
                                        <option value="06">Czerwiec</option>
                                        <option value="07">Lipiec</option>
                                        <option value="08">Sierpień</option>
                                        <option value="09">Wrzesień</option>
                                        <option value="10">Październik</option>
                                        <option value="11">Listopad</option>
                                        <option value="12">Grudzień</option>
                                    </select>
                                    <select 
                                        className="p-2 border border-gray-300 rounded-lg"
                                        value={selectedMonth.split('-')[0]}
                                        onChange={(e) => {
                                            const month = selectedMonth.split('-')[1];
                                            handleMonthChange(`${e.target.value}-${month}`);
                                        }}
                                    >
                                        {Array.from({ length: 10 }, (_, i) => {
                                            const year = new Date().getFullYear() - 5 + i;
                                            return (
                                                <option key={year} value={year}>
                                                    {year}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Informacja o pliku CSV (nazwa PPE, miesiąc) */}
                    {entryMode === 'monthly' && filePPEName && (
                        <div className="mb-4">
                            <p
                                className={
                                    isPPEMatch
                                        ? 'text-green-600 font-semibold'
                                        : 'text-red-600 font-semibold'
                                }
                            >
                                Nazwa PPE z pliku: {filePPEName}
                            </p>
                            <p
                                className={
                                    isMonthMatch
                                        ? 'text-green-600 font-semibold'
                                        : 'text-red-600 font-semibold'
                                }
                            >
                                Miesiąc z pliku: {fileMonth}
                            </p>
                        </div>
                    )}

                    {/* Suwak dni (tylko w monthly) */}
                    {entryMode === 'monthly' && (
                        <div className="mb-4 flex items-center">
                            <button
                                type="button"
                                onClick={() => handleDayChange('prev')}
                                disabled={selectedDay <= 1}
                                className={`px-3 py-2 rounded-l-lg border ${
                                    selectedDay <= 1
                                        ? 'bg-gray-200 cursor-not-allowed'
                                        : 'bg-softTeal text-pureWhite hover:bg-warmOrange'
                                }`}
                            >
                                &#8592;
                            </button>
                            <span className="px-4 py-2 border-t border-b">
                                Dzień {selectedDay}: {formatSelectedDate()}
                            </span>
                            <button
                                type="button"
                                onClick={() => handleDayChange('next')}
                                disabled={selectedDay >= daysInMonth}
                                className={`px-3 py-2 rounded-r-lg border ${
                                    selectedDay >= daysInMonth
                                        ? 'bg-gray-200 cursor-not-allowed'
                                        : 'bg-softTeal text-pureWhite hover:bg-warmOrange'
                                }`}
                            >
                                &#8594;
                            </button>
                        </div>
                    )}

                    {/* Import pliku (tylko monthly) */}
                    {entryMode === 'monthly' && (
                        <div className="mb-4">
                            <label className="block text-gray-700 mb-2">Importuj dane (CSV):</label>
                            <input
                                type="file"
                                accept=".csv"
                                onChange={handleFileImport}
                                className="mb-4"
                            />
                        </div>
                    )}

                    {/* Tabela godzinowa */}
                    <div className="mb-6">
                        <label className="block text-gray-700 mb-2">
                            Punkty danych{' '}
                            {entryMode === 'monthly' ? `(Dzień ${selectedDay})` : ''}
                        </label>
                        <div className="overflow-x-auto">
                            <table className="min-w-full bg-white border border-gray-200">
                                <thead>
                                <tr>
                                    <th className="text-left px-1 py-2">Godzina</th>
                                    {dataPoints.map((_, i) => (
                                        <th key={i} className="border px-1 py-2 text-center w-16">
                                            {i + 1}
                                        </th>
                                    ))}
                                </tr>
                                </thead>
                                <tbody>
                                <tr>
                                    <td className="text-left px-1 py-2">Energia Pobrana</td>
                                    {dataPoints.map((dp, i) => (
                                        <td key={i} className="border px-1 py-2 text-center w-16">
                                            <input
                                                type="text"
                                                className="w-full p-1 rounded-lg bg-transparent appearance-none text-center"
                                                value={dp.consumption}
                                                onChange={(e) => handleInputChange(e, i, 'consumption')}
                                            />
                                        </td>
                                    ))}
                                </tr>
                                <tr>
                                    <td className="text-left px-1 py-2">Energia Oddana</td>
                                    {dataPoints.map((dp, i) => (
                                        <td key={i} className="border px-1 py-2 text-center w-16">
                                            <input
                                                type="text"
                                                className="w-full p-1 rounded-lg bg-transparent appearance-none text-center"
                                                value={dp.production}
                                                onChange={(e) => handleInputChange(e, i, 'production')}
                                            />
                                        </td>
                                    ))}
                                </tr>
                                <tr>
                                    <td className="text-left px-1 py-2">Bilans</td>
                                    {dataPoints.map((dp, i) => (
                                        <td key={i} className="border px-1 py-2 text-center w-16">
                                            <input
                                                type="text"
                                                className="w-full p-1 rounded-lg bg-transparent text-center"
                                                readOnly
                                                value={dp.balance}
                                            />
                                        </td>
                                    ))}
                                </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Przyciski i licznik postępu */}
                    <div className="flex flex-col items-start space-y-4">
                        <div className="flex space-x-4">
                            <button
                                type="button"
                                onClick={handlePasteData}
                                className="bg-transparent text-softTeal font-semibold px-4 py-2 border-2 border-softTeal rounded-lg hover:bg-softTeal hover:text-pureWhite transition"
                                disabled={isSubmitting}
                            >
                                Wklej dane
                            </button>
                            <button
                                type="submit"
                                className={`bg-softTeal text-pureWhite font-semibold px-4 py-2 rounded-lg hover:bg-warmOrange transition ${
                                    isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Przesyłanie...' : 'Zatwierdź dane'}
                            </button>
                            {/* Nowy przycisk "Usuń dane" */}
                            {dataExists && (
                                <button
                                    type="button"
                                    onClick={handleDelete}
                                    className={`bg-black text-pureWhite font-semibold px-4 py-2 rounded-lg hover:bg-red-600 transition ${
                                        isDeleting ? 'opacity-50 cursor-not-allowed' : ''
                                    }`}
                                    disabled={isDeleting}
                                >
                                    {isDeleting ? 'Usuwanie...' : 'Usuń dane'}
                                </button>
                            )}
                        </div>

                        {/* Komunikaty związane z usuwaniem danych */}
                        {deleteMessage && <p className="text-green-500 mt-2">{deleteMessage}</p>}
                        {deleteError && <p className="text-red-500 mt-2">{deleteError}</p>}

                        {/* Komunikat informacyjny */}
                        {entryMode === 'monthly' && dataExists && (
                            <div className="mt-4 p-2 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded">
                                Po zmianie danych za dany miesiąc należy ponownie przetworzyć dane oraz zaktualizować wszystkie raporty od począwszy od miesiąca, gdzie zamiana została wprowadzona.
                            </div>
                        )}

                        {isSubmitting && (
                            <div className="mt-2 text-gray-700">
                                Przetwarzanie dnia {currentDay} z {totalDays}
                            </div>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};

export default DataEntry;
